import { mutate } from 'swr';
import { proxy } from 'valtio';

import { AIConversationHelpers } from '@diagram/helpers/AIConversationHelpers';
import { ValidationService } from '@diagram/services/ValidationService';

import { Diagram } from 'models';
import { StringHelpers } from 'helpers/StringHelpers';

import { InternalTrackingService } from 'services/InternalTrackingService';
import { PayloadSDK } from 'services/PayloadSDK';

import type { DiagramAIMessage } from '@/payload-types';

export const state = proxy({
    view: null as 'history' | 'conversation' | 'create' | 'settings' | null,
    previousView: null as 'history' | 'conversation' | 'create' | 'settings' | null,
    conversation: null as string | null,
    prefilledQuestion: '',
});

const AIConversationService = {
    navigate: {
        back: () => {
            state.view = state.previousView || null;
            state.previousView = null;
        },
        create: () => {
            state.view = 'create';
        },
        conversation: (conversation: string) => {
            state.view = 'conversation';
            state.conversation = conversation;
        },
        history: () => {
            state.view = 'history';
        },
        settings: () => {
            state.previousView = state.view;
            state.view = 'settings';
        },
    },

    history: async (diagramId: Diagram['id']) => {
        return PayloadSDK.find({
            collection: 'diagramAIConversations',
            where: {
                diagram: {
                    equals: diagramId,
                },
            },
            pagination: false,
            sort: '-createdAt',
        });
    },

    messages: async (diagramId: Diagram['id'], conversation: string) => {
        return PayloadSDK.find({
            collection: 'diagramAIMessages',
            where: {
                diagram: {
                    equals: diagramId,
                },
                conversation: {
                    equals: conversation,
                },
            },
            pagination: false,
            sort: 'createdAt',
        });
    },

    start: async (diagram: Diagram, question: string) => {
        InternalTrackingService.track('diagram.ai.startConversation', {
            diagram: diagram.id,
            project: diagram.project,
            question,
        });

        const conversation = await PayloadSDK.create({
            collection: 'diagramAIConversations',
            data: {
                project: diagram.project,
                diagram: diagram.id,
                question,
            },
        });

        await AIConversationService.ask(diagram, conversation.id, question);

        return conversation;
    },

    ask: async (diagram: Diagram, conversation: string, question: string) => {
        InternalTrackingService.track('diagram.ai.ask', {
            diagram: diagram.id,
            project: diagram.project,
            conversation,
            question,
        });

        AIConversationService.clearPrefilledQuestion();

        const message = await PayloadSDK.create({
            collection: 'diagramAIMessages',
            data: {
                question,
                project: diagram.project,
                diagram: diagram.id,
                diagramValidations: ValidationService.getValidationReport(),
                diagramSnapshot: null, // filled on the backend
                conversation,
            },
        });

        await mutate(AIConversationHelpers.swr.messages(diagram.id, conversation), (state) => {
            if (state?.docs) {
                state.docs.push(message);
            }

            return state;
        });
    },

    applyChanges: async (diagram: Diagram, message: DiagramAIMessage) => {
        InternalTrackingService.track('diagram.ai.applyChanges', {
            diagram: diagram.id,
            message,
        });

        await PayloadSDK.update({
            collection: 'diagramAIMessages',
            id: message.id,
            data: {
                changes: {
                    applied: true,
                },
            },
        });
    },

    markChangesAsFailed: async (diagram: Diagram, message: DiagramAIMessage) => {
        InternalTrackingService.track('diagram.ai.markChangesAsFailed', {
            diagram: diagram.id,
            message,
        });

        await PayloadSDK.update({
            collection: 'diagramAIMessages',
            id: message.id,
            data: {
                changes: {
                    applied: true,
                    failed: true,
                },
            },
        });
    },

    execute: async (message: DiagramAIMessage, result: any) => {
        InternalTrackingService.track('diagram.ai.execute', {
            message,
        });

        await PayloadSDK.update({
            collection: 'diagramAIMessages',
            id: message.id,
            data: {
                action: {
                    executed: true,
                    result,
                },
            },
        });
    },

    markExecutionAsFailed: async (message: DiagramAIMessage) => {
        await PayloadSDK.update({
            collection: 'diagramAIMessages',
            id: message.id,
            data: {
                action: {
                    failed: true,
                },
            },
        });
    },

    refresh: async (diagramId: Diagram['id'], conversationId: string) => {
        await mutate(AIConversationHelpers.swr.messages(diagramId, conversationId));
    },

    prefillQuestion: (question: string) => {
        state.prefilledQuestion = StringHelpers.trim(question);
    },

    clearPrefilledQuestion: () => {
        state.prefilledQuestion = '';
    },
};

export { AIConversationService };
export type { DiagramAIMessage };
